import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchSignedJson,
  getEvidenceUrls,
  type AgentTrace,
  type EvidenceDossier,
  type ScoreBreakdown,
  type SignedArtefactUrls,
} from '../lib/artefacts-api';

/**
 * useBriefArtefacts — lazy loader for the three pow-agent artefacts.
 *
 * Each artefact is fetched on demand (call `loadScoreBreakdown()`,
 * `loadEvidenceDossier()`, `loadAgentTrace()`). The signed URLs are
 * shared across the three artefacts and re-requested on 4xx (URL expiry
 * per ADR-029 §Risks) with one retry.
 *
 * Score Breakdown is small (~3KB) and is loaded eagerly on mount so the
 * 4-vector breakdown table on the Brief detail page can render real
 * scores. Evidence Dossier + Agent Trace are loaded on collapsible
 * expand because they can be large (Agent Trace especially).
 *
 * `noAgentRunYet=true` when the Brief has no `latestS3Prefix` — the UI
 * uses this to render the structural placeholder copy instead of trying
 * to fetch.
 */
export interface ArtefactsState<T> {
  status: 'idle' | 'loading' | 'loaded' | 'error' | 'no-agent-run-yet';
  data: T | null;
  error: string | null;
}

type LoaderFn = () => Promise<void>;

interface UseArtefactsResult {
  scoreBreakdown: ArtefactsState<ScoreBreakdown>;
  evidenceDossier: ArtefactsState<EvidenceDossier>;
  agentTrace: ArtefactsState<AgentTrace>;
  loadScoreBreakdown: LoaderFn;
  loadEvidenceDossier: LoaderFn;
  loadAgentTrace: LoaderFn;
  noAgentRunYet: boolean;
}

function initial<T>(): ArtefactsState<T> {
  return { status: 'idle', data: null, error: null };
}

export function useBriefArtefacts(
  applicationId: string | undefined,
  latestS3Prefix?: string | null,
): UseArtefactsResult {
  const [scoreBreakdown, setScoreBreakdown] = useState<ArtefactsState<ScoreBreakdown>>(initial);
  const [evidenceDossier, setEvidenceDossier] = useState<ArtefactsState<EvidenceDossier>>(initial);
  const [agentTrace, setAgentTrace] = useState<ArtefactsState<AgentTrace>>(initial);
  const [noAgentRunYet, setNoAgentRunYet] = useState(false);
  const urlsRef = useRef<SignedArtefactUrls | null>(null);

  // Reset on applicationId change.
  useEffect(() => {
    urlsRef.current = null;
    setScoreBreakdown(initial());
    setEvidenceDossier(initial());
    setAgentTrace(initial());
    setNoAgentRunYet(false);
  }, [applicationId]);

  // When the Brief gains a latestS3Prefix after auto-poll detects the agent
  // has finished, clear any sticky `noAgentRunYet` from the initial pending-
  // state load and reset the artefact states so the eager score-breakdown
  // loader re-fires and the user-expanded panels re-fetch on next open.
  // Must use a ref to call the loader because loadScoreBreakdown isn't
  // declared until later in the function body (hook ordering).
  const pendingReloadRef = useRef(false);
  useEffect(() => {
    if (latestS3Prefix && noAgentRunYet) {
      urlsRef.current = null;
      setNoAgentRunYet(false);
      setScoreBreakdown(initial());
      setEvidenceDossier(initial());
      setAgentTrace(initial());
      pendingReloadRef.current = true;
    }
  }, [latestS3Prefix, noAgentRunYet]);

  /** Get a fresh set of signed URLs, retrying after a refresh on 4xx. */
  const ensureUrls = useCallback(async (forceRefresh = false): Promise<SignedArtefactUrls | null> => {
    if (!applicationId) return null;
    if (!forceRefresh && urlsRef.current !== null) return urlsRef.current;
    try {
      const urls = await getEvidenceUrls(applicationId);
      urlsRef.current = urls;
      return urls;
    } catch (err) {
      if (err instanceof Error && /404.*no_agent_run_yet/.test(err.message)) {
        setNoAgentRunYet(true);
        return null;
      }
      throw err;
    }
  }, [applicationId]);

  function makeLoader<T>(
    pickUrl: (urls: SignedArtefactUrls) => string,
    setState: (s: ArtefactsState<T>) => void,
  ): LoaderFn {
    return async () => {
      setState({ status: 'loading', data: null, error: null });
      try {
        let urls = await ensureUrls(false);
        if (urls === null) {
          setState({ status: 'no-agent-run-yet', data: null, error: null });
          return;
        }
        let data: T;
        try {
          data = await fetchSignedJson<T>(pickUrl(urls));
        } catch (err) {
          const status = (err as { status?: number }).status;
          if (typeof status === 'number' && status >= 400 && status < 500) {
            // URL likely expired — refresh and retry once.
            urls = await ensureUrls(true);
            if (urls === null) {
              setState({ status: 'no-agent-run-yet', data: null, error: null });
              return;
            }
            try {
              data = await fetchSignedJson<T>(pickUrl(urls));
            } catch (retryErr) {
              const retryStatus = (retryErr as { status?: number }).status;
              if (typeof retryStatus === 'number' && retryStatus >= 400 && retryStatus < 500) {
                // Persistent 4xx after URL refresh — the artefact really
                // isn't in S3 (Brief had latestS3Prefix set but the agent
                // never wrote the objects, e.g. seed Briefs, or the
                // retention reaper deleted them). Treat as no-artefacts
                // rather than spamming "Failed to fetch".
                setNoAgentRunYet(true);
                setState({ status: 'no-agent-run-yet', data: null, error: null });
                return;
              }
              throw retryErr;
            }
          } else {
            throw err;
          }
        }
        setState({ status: 'loaded', data, error: null });
      } catch (err) {
        setState({
          status: 'error',
          data: null,
          error: err instanceof Error ? err.message : 'Could not load artefact.',
        });
      }
    };
  }

  // makeLoader is a stable factory defined in this hook body; its only
  // external dep is applicationId which is already in the deps array.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadScoreBreakdown = useCallback(
    makeLoader<ScoreBreakdown>((u) => u.scoreBreakdownUrl, setScoreBreakdown),
    [applicationId],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadEvidenceDossier = useCallback(
    makeLoader<EvidenceDossier>((u) => u.evidenceDossierUrl, setEvidenceDossier),
    [applicationId],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadAgentTrace = useCallback(
    makeLoader<AgentTrace>((u) => u.agentTraceUrl, setAgentTrace),
    [applicationId],
  );

  // Eagerly load score breakdown — small JSON, gates real per-vector
  // scores in the always-visible 4-vector table.
  useEffect(() => {
    if (!applicationId) return;
    void loadScoreBreakdown();
  }, [applicationId, loadScoreBreakdown]);

  // Re-fire the eager loader after the reset effect cleared state. Without
  // this the per-vector cells stay at "—" forever because state went back
  // to 'idle' but nothing pulled fresh data. Watching scoreBreakdown.status
  // is the right trigger: when the reset flips it to 'idle' the consumer
  // signalled they want fresh data.
  useEffect(() => {
    if (pendingReloadRef.current && scoreBreakdown.status === 'idle') {
      pendingReloadRef.current = false;
      void loadScoreBreakdown();
    }
  }, [scoreBreakdown.status, loadScoreBreakdown]);

  return {
    scoreBreakdown,
    evidenceDossier,
    agentTrace,
    loadScoreBreakdown,
    loadEvidenceDossier,
    loadAgentTrace,
    noAgentRunYet,
  };
}
